import { PipelineEnvironment, PipelineStepParams, StepTask } from '@telespot/domain';

import { PipelineStepCategory } from '../../types';
import { PipelineIntegrityService } from '../pipeline-integrity-service';

interface PipelineStepParamsBuildInput {
  env: PipelineEnvironment;
  task: StepTask;
  categories: PipelineStepCategory[];
  multiple: boolean;
  allowNewOptions: boolean;
  enableMosaicView: boolean;
  counter: boolean;
}

const STRUCTURED_PARAMS_KEYS: ReadonlySet<string> = new Set([
  'categorization',
  'options',
  'multiple',
  'allowNewOptions',
  'enableMosaicView',
  'counter',
]);

const MAPPING_BASELINE: PipelineStepParams = {
  options: {},
  categorization: [],
  multiple: false,
  allowNewOptions: false,
  enableMosaicView: false,
  counter: false,
};

export class PipelineStepParamsBuilder {
  /**
   * True when every top-level key in `rawParams` belongs to the structured
   * params schema. False means the params carry data the structured form
   * cannot represent (custom keys authored via Manual Mode), so the editor
   * must open in Manual Mode to avoid silently dropping them.
   */
  public static isStructuredCompatible(rawParams: Record<string, unknown>): boolean {
    return Object.keys(rawParams).every((key) => STRUCTURED_PARAMS_KEYS.has(key));
  }

  public static build(input: PipelineStepParamsBuildInput): PipelineStepParams {
    if (input.task === StepTask.MAPPING) {
      return { ...MAPPING_BASELINE };
    }

    const categorization = input.categories.map((category) => ({
      category: category.uuid,
      options: category.options.map((option) => option.uuid),
    }));

    /**
     * The display threshold is a per-option property of the label inside the
     * step, independent of the task family — persist it for every option.
     */
    const options: PipelineStepParams['options'] = {};
    for (const category of input.categories) {
      for (const option of category.options) {
        options[option.uuid] = { thrdisp: option.thrdis ?? 0 };
      }
    }

    const flags = PipelineIntegrityService.flagsForTask(input.env, input.task, {
      multiple: input.multiple,
      allowNewOptions: input.allowNewOptions,
      enableMosaicView: input.enableMosaicView,
      counter: input.counter,
    });

    return {
      options,
      categorization,
      multiple: flags.multiple,
      allowNewOptions: flags.allowNewOptions,
      enableMosaicView: flags.enableMosaicView,
      counter: flags.counter,
    };
  }
}
